'use client'

import type { ActionResult } from '@mentora/app-shared/i18n/action-result'
import type { AnalyticsConsent, StorefrontId } from '@mentora/data-access/analytics-events'
import { analyticsDistinctId } from '@mentora/data-access/analytics-events'
import posthog from 'posthog-js'
import { useEffect, useRef } from 'react'
import { useConsentStatus } from './use-consent-status'

export interface AnalyticsUser {
  id: string
  role: string | null
  /** The answer stored on the user row; null when never answered while signed in. */
  analyticsConsent: AnalyticsConsent | null
}

export interface AnalyticsIdentityActions {
  saveConsent: (input: { consent: AnalyticsConsent }) => Promise<ActionResult>
}

/**
 * Links the signed-in user to their analytics person, and keeps the server's
 * copy of their banner answer in step with this browser's, since that copy
 * decides whether server events (purchases, refunds) are linked to them.
 * Re-identifies when consent is granted, because an identify sent while opted
 * out is dropped. Only the id and the app role are sent: no name, email or CPF.
 */
export function AnalyticsIdentity({ storefront, user, actions }: {
  storefront: StorefrontId
  user: AnalyticsUser | null
  actions: AnalyticsIdentityActions
}) {
  const status = useConsentStatus()
  const userId = user?.id
  const role = user?.role ?? null
  const stored = user?.analyticsConsent ?? null
  // The session is cookie-cached for minutes, so `stored` can lag a save; this
  // remembers what was already sent so a re-render does not write it again.
  const sent = useRef<string | null>(null)

  useEffect(() => {
    if (status !== 'granted' || !userId)
      return
    const distinctId = analyticsDistinctId(storefront, userId)
    if (posthog.get_distinct_id() !== distinctId)
      posthog.identify(distinctId, { role })
  }, [status, storefront, userId, role])

  useEffect(() => {
    if (!userId || (status !== 'granted' && status !== 'denied') || status === stored)
      return
    const key = `${userId}:${status}`
    if (sent.current === key)
      return
    sent.current = key
    actions.saveConsent({ consent: status })
      .then((result) => {
        if (!result.ok) {
          sent.current = null
          console.warn('[analytics] could not store the consent answer', { errorKey: result.errorKey })
        }
      })
      .catch((error: unknown) => {
        sent.current = null
        console.warn('[analytics] could not store the consent answer', error)
      })
  }, [status, stored, userId, actions])

  return null
}
